'use client';

import { cn } from '@commenda/utils';
import { ChevronDown } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '../Icons';

export type ComboboxWithSearchComponentProps = {
  inputValue: string;
  setInputValue: (arg: string, isCustomValue?: boolean) => unknown;
  options: { value: string; displayedValue: string }[];
  maxHeight?: number;
  error?: string;
  placeholder?: string;
  onChangeInputValue?: (arg: string) => void;
  disabled?: boolean;
  displayValueIfDisabled?: string;
  resetInputValue?: boolean;
  onInputEmpty?: () => void;
  showArrow?: boolean;
  selectedValue?: string;
  hideAddOption?: boolean;
  inputClassName?: string;
};

const ComboboxWithSearch = ({
  inputValue,
  options,
  setInputValue,
  maxHeight,
  error,
  placeholder,
  onChangeInputValue,
  selectedValue,
  disabled,
  displayValueIfDisabled,
  resetInputValue = false,
  inputClassName,
  onInputEmpty,
  showArrow = true,
  hideAddOption = false,
}: ComboboxWithSearchComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const initialOption = options.find((item) => item.value === inputValue);
    setDisplayValue(initialOption?.displayedValue || '');
  }, [inputValue, options]);

  // Memoize filtered items to prevent unnecessary recalculations
  const filteredItems = useMemo(() => {
    if (selectedValue) return options;

    return options.filter((item) =>
      displayValue
        ? item.displayedValue.toLowerCase().includes(displayValue.toLowerCase())
        : true
    );
  }, [options, displayValue, selectedValue]);

  useEffect(() => {
    if (resetInputValue) {
      setDisplayValue('');
    }
  }, [resetInputValue]);

  useEffect(() => {
    const selectedOption = options.find((item) => item.value === inputValue);
    if (selectedOption) {
      setDisplayValue(selectedOption.displayedValue);
    } else {
      setDisplayValue(inputValue);
    }
  }, [inputValue, options]);

  useEffect(() => {
    if (filteredItems?.length === 0 && inputValue && inputValue.length > 0) {
      setHighlightedIndex(0);
    }
  }, [filteredItems, inputValue]);

  useEffect(() => {
    if (!displayValue && displayValue?.length === 0 && inputValue) {
      onInputEmpty?.();
    }
  }, [displayValue, onInputEmpty]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        const container = listRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();

        if (elementRect.top < containerRect.top) {
          container.scrollTop = highlightedElement.offsetTop;
        } else if (elementRect.bottom > containerRect.bottom) {
          container.scrollTop =
            highlightedElement.offsetTop -
            containerRect.height +
            elementRect.height;
        }
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (value: string, isCustomValue = false) => {
    setInputValue(value, isCustomValue);
    const selectedOption = options.find((item) => item.value === value);
    if (selectedOption) {
      setDisplayValue(selectedOption.displayedValue);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLLIElement>
  ) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === filteredItems.length - 1 ? -1 : prevIndex + 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === -1 ? filteredItems.length - 1 : prevIndex - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredItems[highlightedIndex]?.value);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayValue(value);
    if (onChangeInputValue) {
      onChangeInputValue(value);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 relative w-full">
        <input
          disabled={disabled}
          ref={inputRef}
          type="text"
          placeholder={placeholder || 'Add a document'}
          className={cn(
            'flex h-11 w-full txt-medium-regular rounded-md px-3 bg-bg-primary text-text-primary txt-medium-regular placeholder:text-text-placeholder ring-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled hide-date-icon appearance-none hover:bg-bg-primary-hover hover:text-text-primary-hover hover:border-border-primary-hover disabled:border-border-primary',
            inputClassName,
            error
              ? 'border border-border-error focus-visible:border-border-error'
              : 'border border-border-primary focus-visible:border-fg-brand'
          )}
          value={disabled ? displayValueIfDisabled : displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 100)}
          onKeyDown={handleKeyDown}
          aria-controls="combobox-list"
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        {showArrow && (
          <ChevronDown className="size-4 shrink-0 text-fg-quaternary absolute right-3" />
        )}
      </div>
      {isOpen && (
        <ul
          ref={listRef}
          id="combobox-list"
          role="listbox"
          className={`absolute z-10 w-full border rounded bg-white shadow-lg mt-1 overflow-y-auto ${
            (!displayValue && filteredItems.length === 0) ||
            (hideAddOption && filteredItems.length === 0)
              ? 'border-none'
              : ''
          }`}
          {...(maxHeight ? { style: { maxHeight: `${maxHeight}px` } } : {})}
        >
          {displayValue && filteredItems.length === 0 && !hideAddOption && (
            <li
              onClick={() => handleSelect(displayValue, true)}
              onMouseDown={() => handleSelect(displayValue, true)}
              onMouseEnter={() => setHighlightedIndex(0)}
              className="py-2 px-3 w-full cursor-pointer text-neutral-2 body-14-medium flex items-center gap-2 border-b border-border-primary"
            >
              <div className="flex items-center gap-1 justify-start">
                <Icons.Plus className="w-4 h-4" />
                <span className="body-14-medium">Add</span>
                <span className="body-14-medium">{displayValue}</span>
              </div>
            </li>
          )}
          {filteredItems.map((item, index) => (
            <li
              key={index}
              id={`combobox-option-${index}`}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`py-2 px-3 w-full cursor-pointer text-neutral-2 body-14-medium line-clamp-1 text-left text-ellipsis truncate ${
                highlightedIndex === index ? 'bg-neutral-6' : ''
              }`}
              onMouseDown={() => handleSelect(item.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {`${item.displayedValue}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export { ComboboxWithSearch };
